import { useMutation } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Badge, Button, Card, SectionHeader, ErrorState, LoadingState } from '@pixlide/ui';
import { api } from '@/api/client';
import { formatCurrency } from '@/lib/format';
import { usePlans, useSubscription } from '@/api/queries/dashboard';
import {
  CancelSubscriptionButton,
  ResumeSubscriptionButton,
  SUBSCRIPTION_STATUS_PENDING_CANCELLATION,
} from '@/features/billing/SubscriptionActions';
import type { components } from '@/api/generated/schema';

type PlanCode = components['schemas']['PixlideDomainBillingEnumsPlanCode'];

function useStartCheckout() {
  return useMutation({
    mutationFn: async (planCode: PlanCode) => {
      const { data } = await api.POST('/api/v1/billing/checkout', {
        body: {
          planCode,

          // Le webhook Stripe applique l'abonnement de façon asynchrone (voir
          // ProcessStripeWebhookEventCommand) : la page de retour affiche donc un état
          // « en cours d'activation » plutôt que de supposer l'abonnement déjà à jour.
          successUrl: '/transactions?checkout=success',
          cancelUrl: '/offres',
        },
      });

      return data!;
    },

    onSuccess: (checkoutUrl) => {
      window.location.href = checkoutUrl;
    },
  });
}

/** Bandeau « Mon abonnement » : statut courant et date réelle de fin de service. */
function CurrentSubscriptionSummary() {
  const subscription = useSubscription();

  if (!subscription.data) {
    return null;
  }

  const { data } = subscription;
  const isPendingCancellation = data.status === SUBSCRIPTION_STATUS_PENDING_CANCELLATION;

  // Distincte de currentPeriodEnd quand la formule impose un engagement encore en cours :
  // c'est la date à laquelle le service s'arrêtera réellement (voir
  // Subscription.EffectiveCancellationDate côté backend).
  const serviceEndsAt = new Date(
    data.commitmentEndsAt && data.commitmentEndsAt > data.currentPeriodEnd
      ? data.commitmentEndsAt
      : data.currentPeriodEnd,
  ).toLocaleDateString('fr-FR');

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 p-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-ink-700">Mon abonnement</h2>
          {isPendingCancellation ? <Badge tone="warning">Résiliation programmée</Badge> : null}
        </div>

        <p className="text-sm text-ink-500">
          Formule <span className="font-medium text-ink-700">{data.planName}</span>
          {isPendingCancellation ? (
            <>
              {' '}
              — service actif jusqu'au <span className="font-medium text-ink-700">{serviceEndsAt}</span>
            </>
          ) : (
            <> — prochain renouvellement le {serviceEndsAt}</>
          )}
        </p>

        {isPendingCancellation && data.commitmentEndsAt && data.commitmentEndsAt > data.currentPeriodEnd ? (
          <p className="text-xs text-ink-500">
            Cette formule est engagée : le service se poursuit jusqu'au terme de l'engagement,
            au-delà de la période de facturation en cours.
          </p>
        ) : null}
      </div>

      {isPendingCancellation ? <ResumeSubscriptionButton /> : <CancelSubscriptionButton />}
    </Card>
  );
}

/**
 * Page « Nos offres ».
 *
 * Les formules et leurs tarifs viennent de l'API, jamais du code : le board produit
 * prévoit explicitement de les activer, désactiver et retarifer depuis le back-office.
 * Coder une grille tarifaire en dur imposerait un déploiement à chaque ajustement
 * commercial.
 */
export function PlansPage() {
  const plans = usePlans();
  const subscription = useSubscription();
  const startCheckout = useStartCheckout();

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Nos offres"
        description="Changez de formule à tout moment. La facturation est ajustée au prorata."
      />

      {subscription.data ? <CurrentSubscriptionSummary /> : null}

      {plans.isPending ? <LoadingState label="Chargement des offres…" /> : null}

      {plans.isError ? (
        <ErrorState
          message="Impossible de charger les offres pour le moment."
          onRetry={() => void plans.refetch()}
        />
      ) : null}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {(plans.data ?? []).map((plan) => {
          const isCurrent = subscription.data?.planCode === plan.code;

          return (
            <Card
              key={plan.id}
              raised={isCurrent}
              className="flex flex-col gap-4 p-6"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                {isCurrent ? <Badge tone="success">Votre formule</Badge> : null}
              </div>

              <p className="text-3xl font-semibold tracking-tight">
                {formatCurrency(plan.monthlyPrice)}
                <span className="text-sm font-normal text-ink-500"> / mois</span>
              </p>

              {plan.description ? (
                <p className="text-sm text-ink-500">{plan.description}</p>
              ) : null}

              <ul className="flex flex-col gap-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check aria-hidden className="size-4 text-success-500" />
                  {plan.includedScreens} écran{plan.includedScreens > 1 ? 's' : ''} inclus
                </li>

                {plan.includesPremiumTemplates ? (
                  <li className="flex items-center gap-2">
                    <Check aria-hidden className="size-4 text-success-500" />
                    Templates premium
                  </li>
                ) : null}

                {plan.includesAiEnhancement ? (
                  <li className="flex items-center gap-2">
                    <Check aria-hidden className="size-4 text-success-500" />
                    Amélioration IA des photos
                  </li>
                ) : null}

                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <Check aria-hidden className="size-4 text-success-500" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                variant={isCurrent ? 'secondary' : 'primary'}
                disabled={isCurrent || startCheckout.isPending}
                className="mt-auto"
                onClick={() => startCheckout.mutate(plan.code)}
              >
                {isCurrent ? 'Formule actuelle' : 'Choisir cette formule'}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
